import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, inject, ChangeDetectorRef, NgZone } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NgGlyph } from "@ng-icons/core";
import { Subject } from "rxjs";
import { AutocompleteOption } from "./Type/AutocompleteTypes";

/**
 * Autocomplete component with search, filtering, and keyboard navigation.
 * Provides a searchable dropdown with real-time filtering and selection capabilities.
 * Supports both single-select and multi-select modes.
 */
@Component({
  selector: "app-autocomplete",
  standalone: true,
  imports: [CommonModule, FormsModule, NgGlyph],
  templateUrl: "./AutocompleteComponent.html",
  styleUrl: "./AutocompleteComponent.scss",
})
export class AutocompleteComponent implements OnInit, OnDestroy {
  private static nextInputId = 0;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  readonly defaultInputName = `autocomplete-search-${AutocompleteComponent.nextInputId++}`;

  @Input() placeholder = "Search...";
  @Input() inputAutocomplete = "new-password";
  @Input() inputName = "";
  @Input() suppressBrowserAutofill = true;
  @Input() debounceMs = 300;
  @Input() minLength = 2;
  @Input() disabled = false;
  @Input() multiSelect = false;
  @Input() prefetchOnInit = false;
  @Input() loading = false;

  private selectedValueInternal: string | number = "";
  private selectedValuesInternal: (string | number)[] = [];
  private optionsInternal: AutocompleteOption[] = [];
  private internalLoading = false;
  private awaitingFocusLoad = false;
  private loadingTimeout: ReturnType<typeof setTimeout> | null = null;
  private pendingSearchEmitTimeout: ReturnType<typeof setTimeout> | null = null;
  private destroySubject = new Subject<void>();

  searchTerm = "";
  filteredOptions: AutocompleteOption[] = [];
  isOpen = false;
  highlightedIndex = 0;
  isInputReadonly = false;

  get isLoading(): boolean {
    // If we already have options to render, prefer showing them immediately.
    if (this.filteredOptions.length > 0) {
      return this.loading;
    }
    return this.loading || this.internalLoading;
  }

  /**
   * Sets the pre-selected value and updates the search term if a matching option exists.
   * Only used in single-select mode.
   */
  @Input() set selectedValue(value: string | number) {
    this.selectedValueInternal = value;
    this.updateSearchTermFromValue();
  }

  get selectedValue(): string | number {
    return this.selectedValueInternal;
  }

  /**
   * Sets the pre-selected values for multi-select mode.
   * Only used when multiSelect is true.
   */
  @Input() set selectedValues(values: (string | number)[]) {
    this.selectedValuesInternal = values;
  }

  get selectedValues(): (string | number)[] {
    return this.selectedValuesInternal;
  }

  /**
   * Sets the available options and updates the filtered list.
   * Restores the search term if a selectedValue exists.
   */
  @Input() set options(value: AutocompleteOption[]) {
    this.optionsInternal = value;
    this.finishInternalLoading();
    const wasOpen = this.isOpen;
    const searchQuery = this.searchTerm.toLowerCase().trim();
    this.filteredOptions = (() => {
      if (this.searchTerm.length >= this.minLength) {
        return value.filter((option) => option.label.toLowerCase().includes(searchQuery));
      }
      return [...value];
    })();

    if (!this.multiSelect) {
      this.updateSearchTermFromValue();
    }
    this.isOpen = wasOpen;
    this.cdr.detectChanges();
  }

  get options(): AutocompleteOption[] {
    return this.optionsInternal;
  }

  /**
   * Updates the search term to match the label of the currently selected value.
   * Called internally when selectedValue or options change.
   */
  private updateSearchTermFromValue(): void {
    if (!this.selectedValueInternal || this.optionsInternal.length === 0) {
      return;
    }

    const selectedOption = this.optionsInternal.find((option) => option.value === this.selectedValueInternal);

    if (selectedOption) {
      this.searchTerm = selectedOption.label;
      this.cdr.detectChanges();
    }
  }

  /**
   * Emits the selected option value when a selection is made.
   * In multi-select mode, emits the array of selected values.
   */
  @Output() selectionChange = new EventEmitter<string | number | (string | number)[]>();

  /**
   * Emits the current search term when the user types.
   */
  @Output() searchChange = new EventEmitter<string>();

  ngOnInit(): void {
    this.filteredOptions = this.options;
    if (this.suppressBrowserAutofill) {
      this.isInputReadonly = true;
    }
    if (this.prefetchOnInit && this.hasExternalSearchListeners()) {
      this.startInternalLoading();
      this.emitSearchChange("");
    }
  }

  ngOnDestroy(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
    if (this.pendingSearchEmitTimeout) {
      clearTimeout(this.pendingSearchEmitTimeout);
      this.pendingSearchEmitTimeout = null;
    }
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  /**
   * Updates the search term and triggers filtering.
   * @param term - The new search term
   */
  updateSearchTerm(term: string): void {
    this.searchTerm = term;
    this.onSearchInput();
  }

  /**
   * Filters options based on the current search term.
   * Emits searchChange event and updates filteredOptions.
   */
  onSearchInput(): void {
    const hasExternalSearch = this.hasExternalSearchListeners();

    if (hasExternalSearch) {
      this.startInternalLoading();
    }
    this.emitSearchChange(this.searchTerm);

    const searchQuery = this.searchTerm.toLowerCase().trim();

    if (this.searchTerm.length < this.minLength) {
      this.filteredOptions = [...this.options];
      this.isOpen = true;
      this.highlightedIndex = 0;
      this.finishInternalLoading();
      this.cdr.detectChanges();
      return;
    }

    this.filteredOptions = this.options.filter((option) => option.label.toLowerCase().includes(searchQuery));
    this.isOpen = true;
    this.highlightedIndex = 0;
    if (!hasExternalSearch) {
      this.finishInternalLoading();
    }
    this.cdr.detectChanges();
  }

  /**
   * Selects an option, updates the search term, and emits the selection.
   * In multi-select mode, adds to the selection array instead of replacing.
   * @param option - The option to select
   */
  selectOption(option: AutocompleteOption): void {
    if (this.multiSelect) {
      this.selectedValuesInternal = [...this.selectedValuesInternal, option.value];
      this.selectionChange.emit(this.selectedValuesInternal);
      this.searchTerm = "";
      this.filteredOptions = this.options.filter((opt) => !this.selectedValuesInternal.includes(opt.value));
    } else {
      this.searchTerm = option.label;
      this.selectionChange.emit(option.value);
      this.isOpen = false;
      this.filteredOptions = [];
    }
  }

  /**
   * Removes a selected value in multi-select mode.
   * @param value - The value to remove
   */
  removeSelectedValue(value: string | number): void {
    if (!this.multiSelect) {
      return;
    }

    this.selectedValuesInternal = this.selectedValuesInternal.filter((v) => v !== value);
    this.selectionChange.emit(this.selectedValuesInternal);
    this.cdr.detectChanges();
  }

  /**
   * Clears the current selection and reopens the dropdown.
   */
  clearSelection(): void {
    if (this.multiSelect) {
      this.selectedValuesInternal = [];
      this.selectionChange.emit([]);
      this.filteredOptions = [...this.options];
    } else {
      this.searchTerm = "";
      this.filteredOptions = [...this.options];
      this.isOpen = true;
      this.selectionChange.emit("");
    }
  }

  /**
   * Opens the dropdown if not disabled.
   * Only opens if search term meets minLength requirement.
   */
  openDropdown(): void {
    if (this.disabled) {
      return;
    }

    if (this.searchTerm.length < this.minLength) {
      this.filteredOptions = [...this.options];
      this.isOpen = true;
      this.highlightedIndex = 0;
      this.cdr.detectChanges();
      return;
    }

    this.onSearchInput();
  }

  handleFocus(): void {
    if (this.suppressBrowserAutofill && this.isInputReadonly) {
      this.isInputReadonly = false;
    }
    this.openDropdown();
  }

  /**
   * Handles blur event with delayed dropdown closing.
   */
  onBlur(): void {
    setTimeout(() => {
      if (this.awaitingFocusLoad) {
        return;
      }
      this.closeDropdown();
      if (this.suppressBrowserAutofill) {
        this.isInputReadonly = true;
      }
    }, 150);
  }

  /**
   * Closes the dropdown.
   */
  closeDropdown(): void {
    this.isOpen = false;
    this.finishInternalLoading();
    this.awaitingFocusLoad = false;
  }

  private startInternalLoading(): void {
    this.internalLoading = true;
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }

    // Safety fallback: avoid indefinite loading UI if parent options binding misses an update.
    this.loadingTimeout = setTimeout(() => {
      this.finishInternalLoading();
      this.cdr.detectChanges();
    }, 5000);
  }

  private finishInternalLoading(): void {
    this.internalLoading = false;
    this.awaitingFocusLoad = false;
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
  }

  private hasExternalSearchListeners(): boolean {
    return this.searchChange.observers.length > 0;
  }

  private emitSearchChange(term: string): void {
    if (this.pendingSearchEmitTimeout) {
      clearTimeout(this.pendingSearchEmitTimeout);
      this.pendingSearchEmitTimeout = null;
    }
    this.ngZone.runOutsideAngular(() => {
      this.pendingSearchEmitTimeout = setTimeout(() => {
        this.pendingSearchEmitTimeout = null;
        this.ngZone.run(() => this.searchChange.emit(term));
      }, 0);
    });
  }

  /**
   * Sets the highlighted option index for keyboard navigation.
   * @param index - The index to highlight
   */
  highlightOption(index: number): void {
    if (index >= 0 && index < this.filteredOptions.length) {
      this.highlightedIndex = index;
    }
  }

  /**
   * Handles keyboard navigation (Arrow keys, Enter, Escape).
   * @param event - The keyboard event
   */
  onKeydown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      if (event.key === "ArrowDown" || event.key === "Enter") {
        this.openDropdown();
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.highlightOption(Math.min(this.highlightedIndex + 1, this.filteredOptions.length - 1));
        break;

      case "ArrowUp":
        event.preventDefault();
        this.highlightOption(Math.max(this.highlightedIndex - 1, 0));
        break;

      case "Enter":
        event.preventDefault();
        if (this.filteredOptions[this.highlightedIndex]) {
          this.selectOption(this.filteredOptions[this.highlightedIndex]);
        }
        break;

      case "Escape":
        this.closeDropdown();
        break;
    }
  }

  /**
   * Generates a unique ID for a highlighted option element.
   * @param index - The option index
   * @returns The formatted element ID
   */
  getHighlightedId(index: number): string {
    return `autocomplete-option-${index}`;
  }

  /**
   * Checks if a value is selected (for multi-select mode).
   * @param value - The value to check
   * @returns True if the value is selected
   */
  isSelected(value: string | number): boolean {
    return this.selectedValuesInternal.includes(value);
  }

  /**
   * Gets the selected options (for multi-select mode).
   * @returns Array of selected option objects
   */
  getSelectedOptions(): AutocompleteOption[] {
    return this.selectedValuesInternal
      .map((value) => this.optionsInternal.find((option) => option.value === value))
      .filter((option): option is AutocompleteOption => option !== undefined);
  }
}
